import { PWA_SCOPES, getPwaKey, DASHBOARD_KEY } from "../config/pwaScopes";
import { captureInstallPrompt } from "./installPrompt";

const upsertMeta = (attribute, name, content) => {
  let meta = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
};

// Título, íconos y metadata propios de la PWA. En producción el HTML de cada
// PWA ya los trae (scripts/generate-pwa-html.js); esto cubre `npm start` y
// cualquier caso en que se sirva el index.html del dashboard, para que nunca
// se vea "Horus | Dashboard" dentro de una PWA. Lo único que NO se corrige
// aquí en producción es el manifest (ver comentario de registerPwa).
function applyPwaHead(key) {
  const { scope, title, appName, description, themeColor } = PWA_SCOPES[key];

  document.title = title;

  document.head
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((link) => link.remove());

  [
    { rel: "icon", href: `${scope}favicon.ico` },
    { rel: "icon", href: `${scope}icon-192.png`, type: "image/png", sizes: "192x192" },
    { rel: "apple-touch-icon", href: `${scope}icon-192.png` },
  ].forEach((attributes) => {
    const link = document.createElement("link");
    Object.entries(attributes).forEach(([name, value]) => link.setAttribute(name, value));
    document.head.appendChild(link);
  });

  upsertMeta("name", "application-name", title);
  upsertMeta("name", "description", description);
  upsertMeta("name", "theme-color", themeColor);
  upsertMeta("name", "apple-mobile-web-app-title", appName);
  upsertMeta("itemprop", "name", title);
  upsertMeta("itemprop", "description", description);
  document.head.querySelector('meta[itemprop="image"]')?.remove();
}

// Se llama una sola vez, antes del render, desde src/index.js.
//
// El manifest NO se cambia aquí en producción: cada PWA se sirve desde su
// propio HTML (build/<pwa>/index.html, generado por scripts/generate-pwa-html.js)
// que ya trae su <link rel="manifest"> correcto. Cambiarlo por JS era lo que
// hacía que, al instalar, el navegador tomara el manifest del dashboard.
//
// Solo en desarrollo (CRA sirve el mismo index.html para todo) se reapunta el
// manifest para poder probar la PWA con `npm start`.
export function registerPwa() {
  const key = getPwaKey();
  if (key === DASHBOARD_KEY) return;

  const { scope } = PWA_SCOPES[key];

  captureInstallPrompt();
  applyPwaHead(key);

  if (process.env.NODE_ENV !== "production") {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.setAttribute("href", `${scope}manifest.json`);
    }
  }

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .register(`${scope}sw.js`, { scope })
      .catch((error) => {
        console.error(`[${key}] No se pudo registrar el service worker`, error);
      });
  }
}
